import React, { useState } from 'react';
import { BalloonEventModel, DescriptionType, EventDateModel } from 'ts-core';
import { dateCountDown } from '../../utils/dateCountDown';
import { useTheme, useTranslations } from '../..';
import { Info } from './Info';
import { Calendar } from '../Calendar';
import dayjs from 'dayjs';
import { formatDate } from '../../utils/dateCountDown';
import { useIsMobile } from 'react-ui';

function getTruncatedDates(event: BalloonEventModel) {
    const truncatedDates: EventDateModel[] = [];
    let startingIndex = event.dates.findIndex(
        (d) => d.start.getTime() > new Date().getTime()
    );
    if (startingIndex === -1) startingIndex = 0;
    let index = startingIndex;
    while (truncatedDates.length < 5 && index < event.dates.length) {
        truncatedDates.push(event.dates[index]);
        index++;
    }
    index = startingIndex - 1;
    while (truncatedDates.length < 5 && index >= 0) {
        truncatedDates.unshift(event.dates[index]);
        index--;
    }
    return truncatedDates;
}

export function TimeSection({ event }: { event: BalloonEventModel }) {
    const [showMoreDates, setShowMoreDates] = useState(false);
    const { translated, language } = useTranslations();
    const theme = useTheme();
    const timeInfo = event.getDescription(DescriptionType.TimeInformation);
    const isMobile = useIsMobile();

    const dates =
        event.dates.length <= 5 || showMoreDates
            ? event.dates
            : getTruncatedDates(event);

    let nextIndex = dates.findIndex(
        (d) => d.start.getTime() > new Date().getTime()
    );
    if (nextIndex < 0) nextIndex = dates.length - 1;

    const displayDate = (d: EventDateModel) => {
        const isMidnight = (date?: Date) =>
            date instanceof Date && date.getHours() === 0 && date.getMinutes() === 0;

        return d?.isFullDay || isMidnight(d?.realEnd)
            ? `${d.start}${translated('whole_day')}`
            : d.formattedBeginEnd;
    };

    return dates && dates.length > 0 ? (
        <div className="balloon-section time-section flex justify-between flex-wrap">
            <div className="flex flex-col flex-1">
                <div className="flex flex-col max-h-[320px] overflow-y-auto">
                    {dates
                        .sort((a, b) => a.start.getTime() - b.start.getTime())
                        .map((d, i) => (
                            <div key={i} className="flex flex-row items-center mb-2">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth={i === nextIndex ? '1.5' : '1'}
                                    stroke="currentColor"
                                    className="w-6 h-6 mx-2"
                                    style={{
                                        color: i === nextIndex ? theme.primaryColor : 'gray',
                                    }}
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
                                    />
                                </svg>

                                <div className="flex flex-col ">
                                    {i === nextIndex && (
                                        <span style={{ display: 'inline-block', float: 'left' }}>
                                            {dateCountDown(d, translated)}
                                        </span>
                                    )}

                                    <span
                                        style={{
                                            display: 'inline-block',
                                            float: 'left',
                                            color: i === nextIndex ? 'inherit' : 'gray',
                                        }}
                                    >
                                        {d.realEnd ? d.formattedBeginEnd : formatDate(d.start)}
                                    </span>
                                    <span
                                        style={{
                                            display: 'inline-block',
                                            float: 'left',
                                            color: i === nextIndex ? 'inherit' : 'gray',
                                        }}
                                    >
                                        {d.note}
                                    </span>
                                </div>
                            </div>
                        ))}
                </div>
                {event.dates.length > 5 && (
                    <button
                        onClick={() => setShowMoreDates((t) => !t)}
                        className="text-sm text-gray-500 mt-2"
                    >
                        {showMoreDates
                            ? translated('hide_more_dates')
                            : translated('show_more_dates')}
                    </button>
                )}
                {timeInfo && <Info info={timeInfo.getText(language)} />}
            </div>
            {/* Only render the calendar for multi-date events. A single-date
                event doesn't benefit from a month grid — the date list above
                carries all the info — and the empty right column wastes space. */}
            {!isMobile && event.dates.length > 1 && (
                <Calendar
                    className="block w-[300px] border-l border-gray-200 px-2"
                    selectedDates={event.dates.map((d) => [
                        d.start,
                        dayjs(d.end).isValid() ? d.end! : d.start,
                    ])}
                />
            )}
        </div>
    ) : null;
}
